import * as React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { MentionsInput, Mention, SuggestionDataItem } from "react-mentions";
import { Text, Caption1, makeStyles, tokens } from "@fluentui/react-components";
import { RichTextValue, MentionUser, MentionCell, MentionReference } from "./richtext/types";
import { markupToPlainText } from "./richtext/parser";
import { ExcelService } from "../../../services/excel.service";
import { isStandaloneMode } from "../../../utils/standalone-mode";
import { getOrganizationUsers } from "../../../services/users.service";
import { StatusItemDto } from "../../../services/status-items.service";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  characterCounter: {
    display: "flex",
    justifyContent: "flex-end",
  },
  characterCounterError: {
    color: tokens.colorPaletteRedForeground1,
  },
});


// aria-label of the react-mentions suggestions listbox — set via the
// `a11ySuggestionsListLabel` prop on <MentionsInput> below. Also used by
// the module-scope Tab handler to scope its DOM query to react-mentions
// dropdowns only (Fluent Combobox uses the same role="listbox" +
// role="option" ARIA pattern, so an unscoped query hijacks Tab whenever
// a Combobox is mounted with a selection — e.g. the Job Type field on
// the Setup page). Keep this string in sync between both callers.
const MENTIONS_LISTBOX_LABEL = "Suggestions";

// Module-level Tab-to-select handler for react-mentions dropdowns.
//
// Registered once on module load — critical for the CreateDiscussionDialog
// case: Fluent Dialog installs a window-capture keydown listener with
// stopImmediatePropagation when a Dialog mounts, and same-target-same-phase
// listeners fire in REGISTRATION ORDER. Registering inside a component's
// useEffect means we bind AFTER Fluent (Dialog is the ancestor, mounts
// first), so Fluent's stop-immediate pre-empts us. Registering at module
// scope binds during app-boot module evaluation, well before any Dialog
// mounts, so we go first and Fluent registers behind us.
//
// Guard for tests / SSR where `window` isn't defined.
if (typeof window !== "undefined") {
  window.addEventListener(
    "keydown",
    (e) => {
      if (e.key !== "Tab" || e.shiftKey) return;
      // Scope the query to the react-mentions listbox specifically —
      // both react-mentions AND Fluent Combobox render options with
      // role="option" aria-selected="true", so an unscoped selector
      // hijacks Tab whenever any Combobox with a selection is mounted
      // (the Job Type field on Setup regressed exactly this way when it
      // changed from a plain input to a Combobox). The `aria-label` on
      // the mentions <ul> comes from our `a11ySuggestionsListLabel` prop
      // (see MentionsInput below); Fluent's Combobox listbox takes its
      // aria-label from the field's label, so this scoping cleanly
      // distinguishes the two.
      const focused = document.querySelector<HTMLElement>(
        `[role='listbox'][aria-label='${MENTIONS_LISTBOX_LABEL}'] [role='option'][aria-selected='true']`,
      );
      if (!focused) return;
      // Only claim Tab when a suggestion is actually focused. Otherwise
      // return so Fluent's focus trap can do its normal work — critical
      // to preserve dialog focus-cycling behaviour when the dropdown
      // isn't showing.
      e.preventDefault();
      e.stopImmediatePropagation();
      focused.click();
    },
    true,
  );
}

interface RichTextInputProps {
  /** Current value with both markup and plain text */
  value: RichTextValue;
  /** Callback when value changes */
  onChange: (value: RichTextValue) => void;

  // Configuration
  placeholder?: string;
  maxLength?: number;
  minHeight?: string;
  disabled?: boolean;
  /** Hint text displayed below the input, inline with the character counter */
  hintText?: React.ReactNode;

  // Validation
  validationMessage?: string;

  // Optional custom data sources
  /** List of users for @mentions */
  users?: MentionUser[];
  /** List of status items for #mentions */
  statusItems?: StatusItemDto[];

  // Features
  enableUserMentions?: boolean;
  enableCellMentions?: boolean;
  enableReferenceMentions?: boolean;
}

/**
 * Rich text input component with support for user mentions (@) and cell references ([])
 *
 * Features:
 * - User mentions: Type @ to mention users by name/email
 * - Cell references: Type [ to reference Excel cells (e.g., Sheet1!A1)
 * - Current selection: Shows the currently selected cell at the top of the dropdown
 * - Character counter: Displays character count with visual feedback when over limit
 *
 * The component stores text in markup format: "@[Display](id)" for both mentions and cells
 */
/**
 * Rich-text input with three trigger characters:
 *   `@` mentions a user, `#` references a status item, `!` references a sheet.
 * Built on react-mentions; emits a markup string with `@[Display](id)` /
 * `#[Display](type:value)` tokens that {@link RichTextDisplay} renders back.
 */
const RichTextInput: React.FC<RichTextInputProps> = ({
  value,
  onChange,
  placeholder = "Type @ to mention users, # to tag sheets/items, [ to reference cells...",
  maxLength = 500,
  minHeight,
  disabled = false,
  hintText,
  validationMessage,
  users,
  statusItems,
  enableUserMentions = true,
  enableCellMentions = true,
  enableReferenceMentions = true,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const [internalValue, setInternalValue] = useState(value.markup);
  const [currentSelection, setCurrentSelection] = useState<MentionCell | null>(null);
  const [orgUsers, setOrgUsers] = useState<MentionUser[]>(users || []);
  const [sheets, setSheets] = useState<{ id: string; name: string }[]>([]);

  // Sync external value changes (e.g., when parent resets the form)
  // Note: internalValue is intentionally not in deps to avoid circular updates
  useEffect(() => {
    if (value.markup !== internalValue) {
      setInternalValue(value.markup);
    }
  }, [value.markup]);

  // Function to fetch current cell selection dynamically (called when [ is typed).
  // Requires an Excel host — in standalone/dialog we return no suggestions,
  // and the `[` picker just shows nothing (users can still type the reference
  // manually if they know it).
  const fetchCellSuggestions = async (
    _query: string,
    callback: (data: SuggestionDataItem[]) => void
  ) => {
    if (isStandaloneMode()) {
      callback([]);
      return;
    }
    try {
      const selection = await ExcelService.getSelectedCell();
      const cellData: MentionCell = {
        id: selection.fullReference,
        display: selection.fullReference,
        sheet: selection.sheet,
      };
      setCurrentSelection(cellData);
      callback([{ id: cellData.id, display: cellData.display }]);
    } catch (error) {
      console.error("Failed to get current selection:", error);
      callback([]);
    }
  };

  // Fetch organization users from Microsoft Graph
  useEffect(() => {
    // If users were provided via props, use those instead
    if (users && users.length > 0) {
      setOrgUsers(users);
      return;
    }

    // Otherwise, fetch from Microsoft Graph
    const fetchUsers = async () => {
      try {
        const fetchedUsers = await getOrganizationUsers();
        setOrgUsers(fetchedUsers);
      } catch (error) {
        console.error("Failed to fetch organization users:", error);
        setOrgUsers([]);
      }
    };

    fetchUsers();
  }, [users]);

  // Fetch sheets from Excel. Standalone/dialog have no Excel — the sheet
  // suggestion list stays empty, and the `#sheet:` reference picker will
  // just show no options.
  useEffect(() => {
    if (!enableReferenceMentions) return;
    if (isStandaloneMode()) {
      setSheets([]);
      return;
    }

    const fetchSheets = async () => {
      try {
        const worksheetInfos = await ExcelService.getAllWorksheetsInfo();
        setSheets(worksheetInfos);
      } catch (error) {
        console.error("Failed to fetch worksheet info:", error);
        setSheets([]);
      }
    };

    fetchSheets();
  }, [enableReferenceMentions]);

  const handleChange = (_event: any, newValue: string) => {
    setInternalValue(newValue);

    // Update parent with both markup and plain text
    onChange({
      markup: newValue,
      plainText: markupToPlainText(newValue),
    });
  };

  const characterCount = internalValue.length;
  const isOverLimit = characterCount > maxLength;

  // Reference to the container — anchor for the capture-phase Tab handler
  // below, and the scope we search for the currently-focused suggestion.
  const containerRef = useRef<HTMLDivElement>(null);

  // Tab-to-select for the mentions dropdown is registered at MODULE SCOPE
  // (see the top of this file). Not in a useEffect: Fluent Dialog's focus
  // trap installs a window-capture listener with stopImmediatePropagation
  // when a Dialog mounts, and same-target-same-phase listeners fire in
  // REGISTRATION ORDER. Registering during a component's mount effect
  // means we register AFTER Fluent (Dialog is our ancestor, mounts first),
  // so Fluent's stopImmediatePropagation pre-empts us. Registering at
  // module load — which happens when this file is first imported at app
  // boot — puts us first, and Fluent's later mount registers behind us.

  // Retained for the "suggestions closed" case — react-mentions calls
  // props.onKeyDown only when its dropdown isn't showing, so this is where
  // future keydown-in-textarea logic would live. No-op today.
  const handleKeyDown = useCallback((_event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    // intentionally empty
  }, []);

  // Convert users to suggestion format
  const userSuggestions: SuggestionDataItem[] = orgUsers.map((user) => ({
    id: user.id,
    display: user.display,
  }));

  // Build reference suggestions (sheets + status items)
  const referenceSuggestions: any[] = [];

  // Add sheets (store ID in markup, display name to user)
  sheets.forEach((sheet) => {
    referenceSuggestions.push({
      id: `sheet:${sheet.id}`,
      display: sheet.name,
      type: 'sheet',
    });
  });

  // Add status items
  if (statusItems) {
    statusItems.forEach((item) => {
      referenceSuggestions.push({
        id: `statusItem:${item.id}`,
        display: item.name,
        type: 'statusItem',
      });
    });
  }

  // Inline styles for react-mentions (Griffel doesn't support nested selectors)
  const mentionsStyle = {
    control: {
      fontSize: tokens.fontSizeBase300,
      fontFamily: tokens.fontFamilyBase,
      minHeight: minHeight || "80px",
      position: "relative" as const,
      display: "block",
    },
    input: {
      minHeight: minHeight || "80px",
      maxHeight: "200px",
      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
      backgroundColor: tokens.colorNeutralBackground1,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      borderRadius: tokens.borderRadiusMedium,
      outline: "none",
      resize: "none" as const,
      overflow: "auto",
      boxSizing: "border-box" as const,
    },
    highlighter: {
      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
      border: "1px solid transparent",
      boxSizing: "border-box" as const,
      overflow: "hidden",
      maxHeight: "200px",
    },
    suggestions: {
      list: {
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow16,
        maxHeight: "200px",
        overflowY: "auto" as const,
        position: "fixed" as const,
        zIndex: 10000,
      },
      item: {
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        fontSize: tokens.fontSizeBase300,
        "&focused": {
          backgroundColor: tokens.colorNeutralBackground1Selected,
        },
      },
    },
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <div>
        <MentionsInput
          value={internalValue}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          style={mentionsStyle}
          a11ySuggestionsListLabel={MENTIONS_LISTBOX_LABEL}
          onKeyDown={handleKeyDown}
          // Disarm writing-assistant and password-manager overlays on the
          // underlying textarea. All of these have been observed swallowing
          // arrow-key / Enter keydown events before the mentions library
          // sees them, which manifests as "some users can't navigate the
          // mentions dropdown". These props are forwarded to the internal
          // <textarea>.
          spellCheck={false}
          data-gramm="false"                    // Grammarly (browser + desktop)
          data-gramm_editor="false"             // Grammarly rich-text editor
          data-enable-grammarly="false"         // Grammarly opt-out variant
          data-lpignore="true"                  // LastPass
          data-1p-ignore="true"                 // 1Password
          data-form-type="other"                // Dashlane
          data-bwignore="true"                  // Bitwarden
        >
          {/*
            react-mentions types MentionsInput.children narrowly as
            ReactElement<MentionProps>, so the natural
            `{cond && <Mention/>}` pattern produces `false | Element` which
            strict-mode rejects. Wrapping in an array + filter(Boolean)
            and casting through the expected element type satisfies the
            compiler without changing runtime behaviour — `false` entries
            are already filtered out.
          */}
          {([
            enableUserMentions && (
              <Mention
                key="user"
                trigger="@"
                appendSpaceOnAdd={true}
                data={userSuggestions}
                displayTransform={(_id, display) => `@${display}`}
                markup="@[__display__](__id__)"
                renderSuggestion={(suggestion) => (
                  <div>
                    <strong>{suggestion.display}</strong>
                    {suggestion.id && (
                      <div style={{ fontSize: "0.85em", color: "#666" }}>
                        {suggestion.id}
                      </div>
                    )}
                  </div>
                )}
              />
            ),
            enableCellMentions && (
              <Mention
                key="cell"
                trigger="["
                data={fetchCellSuggestions}
                displayTransform={(_id, display) => `[${display}]`}
                markup="[__display__](__id__)"
                appendSpaceOnAdd
                renderSuggestion={(suggestion) => (
                  <div style={{ fontFamily: "monospace" }}>
                    <strong>{suggestion.display}</strong>
                    <span style={{ marginLeft: "8px", fontSize: "0.85em", color: "#666" }}>
                      (current selection)
                    </span>
                  </div>
                )}
              />
            ),
            enableReferenceMentions && (
              <Mention
                key="reference"
                trigger="#"
                data={referenceSuggestions}
                displayTransform={(_id, display) => `#${display}`}
                markup="#[__display__](__id__)"
                appendSpaceOnAdd
                renderSuggestion={(suggestion: any) => (
                  <div>
                    <strong>{suggestion.display}</strong>
                    <div style={{ fontSize: "0.85em", color: "#666" }}>
                      {suggestion.type === 'sheet' ? 'Sheet' : 'Status Item'}
                    </div>
                  </div>
                )}
              />
            ),
          ].filter(Boolean) as React.ReactElement[])}
        </MentionsInput>
      </div>

      <div className={styles.footer}>
        {hintText ? (
          <Text className={typography.caption}>{hintText}</Text>
        ) : (
          <span />
        )}
        <Text
          className={
            isOverLimit
              ? `${typography.caption} ${styles.characterCounter} ${styles.characterCounterError}`
              : `${typography.caption} ${styles.characterCounter}`
          }
        >
          {characterCount} / {maxLength}
        </Text>
      </div>

      {validationMessage && (
        <Text className={typography.errorText}>{validationMessage}</Text>
      )}
    </div>
  );
};

export default RichTextInput;
